import React, { useState, useRef, useEffect } from "react";
import DatePickerComponent from "../datepicker/DatePicker";
import Modal from "../modal/Modal";
import PrimaryButton from "../buttons/PrimaryButton";
import "../../styles/recordedData/recordedData.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import "../../styles/cards/card.css";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import axios from "axios";
import Cookies from "js-cookie";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";
import ScannerOperatorsTable from "../sectorsTable/ScannerOperatorsTable";

const ScannerOperatorsComponent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState(new Date("2024-01-01"));
  const [endDate, setEndDate] = useState(new Date());
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [scannerOperators, setScannerOperators] = useState([]);
  const [selectedScannerOperator, setSelectedScannerOperator] = useState("");
  const [showAssignConfirmationModal, setShowAssignConfirmationModal] =
    useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editAssignment, setEditAssignment] = useState(null);
  const [originalScannerOperatorId, setOriginalScannerOperatorId] = useState("");
  const [originalSiteId, setOriginalSiteId] = useState("");

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const roleAssigned = Cookies.get("role");
  useEffect(() => {
    if (showAssignModal) {
      const fetchSites = async () => {
        try {
          const token = Cookies.get("token");
          const response = await axios.get(`${baseUrl}/api/sites`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const sitesData = response.data;
          setSites(sitesData);

          const uniqueProvinces = [
            ...new Set(sitesData.map((site) => site.province)),
          ].sort();
          const uniqueDistricts = [
            ...new Set(sitesData.map((site) => site.district)),
          ].sort();
          const uniqueSectors = [
            ...new Set(
              sitesData
                .map((site) => site.sector)
                .filter((sector) => sector !== null)
            ),
          ].sort();

          setProvinces(uniqueProvinces);
          setDistricts(uniqueDistricts);
          setSectors(uniqueSectors);
        } catch (error) {
          setError("Failed to load location data");
        }
      };

      const fetchScannerOperators = async () => {
        try {
          const token = Cookies.get("token");
          const response = await axios.get(
            `${baseUrl}/api/users/scanner-operators`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setScannerOperators(response.data);
        } catch (error) {
          setError("Failed to load scanner operators");
        }
      };

      fetchSites();
      fetchScannerOperators();
    }
  }, [
    showAssignModal,
    baseUrl,
    selectedScannerOperator,
  ]);
  const handleStartDateChange = (date) => {
    setStartDate(date);
  };
  const handleEndDateChange = (date) => {
    setEndDate(date);
  };
  const handleAssignScannerOperatorModal = () => {
    setShowAssignModal(true);
    setIsEditing(false);
    setSelectedProvince("");
    setSelectedDistrict("");
    setSelectedSector("");
    setSelectedScannerOperator("");
    setOriginalScannerOperatorId("");
    setOriginalSiteId("");
    setError("");
  };

  const handleProvinceChange = (e) => {
    setSelectedProvince(e.target.value);
    setSelectedDistrict("");
    setSelectedSector("");
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setSelectedSector("");
  };

  const handleAssignClick = (assignment) => {
    setIsEditing(true);
    setEditAssignment(assignment);
    setSelectedScannerOperator(assignment.operatorId);
    setSelectedProvince(assignment.province || "");
    setSelectedDistrict(assignment.district || "");
    setSelectedSector(assignment.sector || "");
    setOriginalScannerOperatorId(assignment.operatorId.toString());
    setOriginalSiteId(assignment.siteId?.toString() || "");
    setShowAssignModal(true);
    setError("");
  };

  const handleSectorChange = (e) => {
    setSelectedSector(e.target.value);
  };

  // Handle scanner operator selection
  const handleScannerOperatorChange = (e) => {
    setSelectedScannerOperator(e.target.value);
  };

  // Handle assignment submission
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProvince || !selectedDistrict || !selectedScannerOperator) {
      setError("Please select province, district, and scanner operator");
      return;
    }

    // Find siteId based on selected province, district, and sector
    const selectedSite = sites.find(
      (site) =>
        site.province === selectedProvince &&
        site.district === selectedDistrict &&
        (selectedSector ? site.sector === selectedSector : site.sector == null)
    );

    if (!selectedSite) {
      setError("Selected site not found");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const token = Cookies.get("token");
      const method = isEditing ? "put" : "post";
      const url = `${baseUrl}/api/users/scanner-operator/assign`;
      const data = isEditing
        ? {
            scannerOperatorId: parseInt(originalScannerOperatorId),
            siteId: parseInt(originalSiteId),
            newScannerOperatorId: parseInt(selectedScannerOperator),
            newSiteId: selectedSite.id,
          }
        : {
            scannerOperatorId: parseInt(selectedScannerOperator),
            siteId: selectedSite.id,
          };

      const response = await axios({
        method,
        url,
        data,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 || response.status === 201) {
        setShowAssignModal(false);
        setShowAssignConfirmationModal(true);
        setSelectedProvince("");
        setSelectedDistrict("");
        setSelectedSector("");
        setSelectedScannerOperator("");
        setOriginalScannerOperatorId("");
        setOriginalSiteId("");
        setEditAssignment(null);
        setIsEditing(false);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 409) {
          setError("Scanner operator already assigned to this site");
        } else if (err.response.status === 400) {
          setError(err.response.data.error || "Invalid input");
        } else {
          setError(
            isEditing
              ? "Failed to update scanner operator assignment"
              : "Failed to assign scanner operator"
          );
        }
      } else {
        setError("Server is not responding. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (showAssignConfirmationModal) {
      const timer = setTimeout(() => {
        setShowAssignConfirmationModal(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showAssignConfirmationModal]);

  const handleCloseAssignScannerOperatorModal = () => {
    setShowAssignModal(false);
    setSelectedProvince("");
    setSelectedDistrict("");
    setSelectedSector("");
    setSelectedScannerOperator("");
    setOriginalScannerOperatorId("");
    setOriginalSiteId("");
    setEditAssignment(null);
    setIsEditing(false);
    setError("");
  };

  useEffect(() => {
    setFilteredData(
      data.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, data]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  return (
    <div className="recorded-data-content">
      <div className="title">SCANNER OPERATORS</div>

      <div className="search-and-upload">
        <div className="search-and-date">
          <div className="search-container">
            <div className="search">
              <div className="search-icon">
                <Icon
                  icon="proicons:search"
                  color="#078ECE"
                  width="16"
                  height="16"
                />
              </div>
              <input
                type="text"
                placeholder="Search scanner operators"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="date-picker-container">
            <DatePickerComponent
              onDateChange={handleStartDateChange}
              selectedDate={startDate}
              search={true}
            />
            <DatePickerComponent
              onDateChange={handleEndDateChange}
              selectedDate={endDate}
              isEndDate={true}
              startDate={startDate}
            />
          </div>
        </div>
        <div className="upload-buttons">
          <PrimaryButton
            text="Assign Scanner Operators"
            icon="si:add-fill"
            onClick={handleAssignScannerOperatorModal}
          />
        </div>
      </div>

      <Modal
        show={showAssignModal}
        onClose={handleCloseAssignScannerOperatorModal}
      >
        <div className="title">
          {isEditing
            ? "EDIT SCANNER OPERATOR ASSIGNMENT"
            : "ASSIGN SCANNER OPERATOR"}
        </div>
        <div className="content">
          <div className="form-container assigned">
            <form onSubmit={handleAssignSubmit}>
              <div className="label">
                <div>Province</div>
                <select
                  name="province"
                  value={selectedProvince}
                  onChange={handleProvinceChange}
                  required
                >
                  <option value="" disabled>
                    Select Province
                  </option>
                  {provinces.map((province) => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))}
                </select>
              </div>
              <div className="label">
                <div>District</div>
                <select
                  name="district"
                  value={selectedDistrict}
                  onChange={handleDistrictChange}
                  disabled={!selectedProvince}
                  required
                >
                  <option value="" disabled>
                    Select District
                  </option>
                  {districts
                    .filter((district) =>
                      sites.some(
                        (site) =>
                          site.province === selectedProvince &&
                          site.district === district
                      )
                    )
                    .map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                </select>
              </div>
              <div className="label">
                <div>Sector</div>
                <select
                  name="sector"
                  value={selectedSector}
                  onChange={handleSectorChange}
                  disabled={!selectedDistrict}
                >
                  <option value="" disabled>
                    Select Sector(Optional)
                  </option>
                  <option value="">No Sector</option>
                  {sectors
                    .filter((sector) =>
                      sites.some(
                        (site) =>
                          site.province === selectedProvince &&
                          site.district === selectedDistrict &&
                          site.sector === sector
                      )
                    )
                    .map((sector) => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                </select>
              </div>
              <div className="label">
                <div>Scanner Operator</div>
                <select
                  name="scannerOperator"
                  value={selectedScannerOperator}
                  onChange={handleScannerOperatorChange}
                  required
                >
                  <option value="" disabled>
                    Select Scanner Operator
                  </option>
                  {scannerOperators.map((operator) => (
                    <option key={operator.id} value={operator.id}>
                      {operator.name}
                    </option>
                  ))}
                </select>
              </div>
              {error && <div className="error-message">{error}</div>}
            </form>
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                loading ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : isEditing ? (
                  "Update"
                ) : (
                  "Assign"
                )
              }
              onClick={handleAssignSubmit}
            />
            <PrimaryButton
              text="Cancel"
              onClick={handleCloseAssignScannerOperatorModal}
            />
          </div>
        </div>
      </Modal>

      <Modal
        show={showAssignConfirmationModal}
        onClose={() => setShowAssignConfirmationModal(false)}
      >
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm" width="57" height="57" />
          </div>
          <div className="text">
            {isEditing
              ? "Scanner operator assignment updated successsfully!"
              : "Scanner operator assigned successfully!"}
          </div>
        </div>
      </Modal>

      <div className="table-container">
        <div className="table-padding-wrapper">
          <ScannerOperatorsTable
            searchQuery={searchQuery}
            refreshTrigger={refreshTrigger}
            onAssignClick={handleAssignClick}
          />
        </div>
      </div>
    </div>
  );
};

export default ScannerOperatorsComponent;
